import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'primereact/button'
import { Trans, useTranslation } from 'react-i18next'
import "./abonnementCard.css";
import { IoCheckmark } from "react-icons/io5";
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { enumStatutAbonnement } from "../../enums/enumStatutAbonnement";
import { enumChooseSubscription } from '../../enums/enumChooseSubscription';
import StripeService from '../../services/stripeService';
import EntrepriseService from '../../services/entrepriseService';

export const AbonnementCard = (props) => {

    const { t } = useTranslation();
    const history = useHistory();
    const toast = useRef(null);
    
    // Instantiate the services only once
    const stripeService = useRef(new StripeService()).current;
    const entrepriseService = useRef(new EntrepriseService()).current;
    
    const [inputSwitchChecked, setInputSwitchChecked] = useState(false);
    const [billingDate, setBillingDate] = useState(null);

    const adminImpersonatingRecruteurId = useMemo(() => 
        history.location.state?.adminImpersonatingRecruteurId, 
        [history.location.state]
    );

    const isRegularOrCasual = useMemo(() => 
        props.enumChooseSubscription === enumChooseSubscription.REGULAR || 
        props.enumChooseSubscription === enumChooseSubscription.CASUAL,
        [props.enumChooseSubscription]
    );

    const determineIndexFromAbonnementType = useCallback(() => {
        try {
            // Normalize abonnementType: backend may send enum as string (e.g. "3") or number
            const rawType = props.abonnementType;
            const normalizedType = (typeof rawType === 'string' && !isNaN(Number(rawType))) ? Number(rawType) : rawType;
            const options = Array.isArray(props.pricingOptions) ? props.pricingOptions : [];
            const normalizeLabel = (s) => (s || '').toString().toLowerCase();

            const findIndexForType = (type) => {
                for (let i = 0; i < options.length; i++) {
                    const label = normalizeLabel(options[i].label);
                    if (type === enumChooseSubscription.CASUAL) {
                        if (label.includes('60') || label.includes('casual') || label.includes('découverte') || label.includes('decouverte')) return i;
                    }
                    if (type === enumChooseSubscription.REGULAR) {
                        if (label.includes('240') || label.includes('regular') || label.includes('recruteur')) return i;
                    }
                }
                return -1;
            };

            if (props.isCurrent && props.abonnementType !== undefined && props.abonnementType !== null) {
                const idx = findIndexForType(normalizedType);
                if (idx >= 0) return idx;
                // fallback: REGULAR -> prefer second option if exists
                if (normalizedType === enumChooseSubscription.REGULAR) return (options.length > 1 ? 1 : 0);
                if (normalizedType === enumChooseSubscription.CASUAL) return 0;
            }
        } catch (e) {
            // ignore and fallback to 0
        }
        return 0;
    }, [props.abonnementType, props.isCurrent, props.pricingOptions]);

    const [selectedPricingIndex, setSelectedPricingIndex] = useState(() => determineIndexFromAbonnementType());

    const expectedIndexForCompany = useMemo(() => 
        determineIndexFromAbonnementType(), 
        [determineIndexFromAbonnementType]
    );

    const showSwitchArea = useMemo(() => 
        isRegularOrCasual && !!props.isCurrent && (selectedPricingIndex === expectedIndexForCompany),
        [isRegularOrCasual, props.isCurrent, selectedPricingIndex, expectedIndexForCompany]
    );

    const loadData = useCallback(() => {
        if(props.searchNextBillingDate) {
            stripeService.getNextBillingDate(adminImpersonatingRecruteurId).then(res => {
                setBillingDate(res);
            })
            .catch(err => {
                setBillingDate(null);
            });
        }
        // Initialize the switch state only for current REGULAR/CASUAL offers
        if (isRegularOrCasual && props.isCurrent) {
            entrepriseService.getStatutAbonnementEntreprise(adminImpersonatingRecruteurId).then(res => {
                setInputSwitchChecked(res === enumStatutAbonnement.EN_COURS);
            })
            .catch(err => {
                setInputSwitchChecked(false);
            });
        }
    }, [props.searchNextBillingDate, isRegularOrCasual, props.isCurrent, adminImpersonatingRecruteurId, stripeService, entrepriseService]);

    // Synchronize the index with the subscription type
    useEffect(() => {
        if (props.isCurrent && props.abonnementType) {
            setSelectedPricingIndex(determineIndexFromAbonnementType());
        }
    }, [props.abonnementType, props.isCurrent, determineIndexFromAbonnementType]);

    // Load data on mount
    useEffect(() => {
        loadData();
    }, [loadData]);

    const onChangeInputSwitch = useCallback((e) => {
        stripeService.updateSubscriptionEndOfPeriod(e.value, adminImpersonatingRecruteurId).then(() => {
            setInputSwitchChecked(e.value);
        })
        .catch(err => {
            toast.current.show({ severity: 'error', summary: t('general.failed'), detail: t('error.updating_subscription_end_of_period_error') });
        });
    }, [stripeService, adminImpersonatingRecruteurId, t]);

    const renderDescription = useCallback(() => {
        if (!props.enumChooseSubscription) {
            return props.description ? <div className='ac-card-subtitle'>{props.description}</div> : null;
        }

        switch (props.enumChooseSubscription) {
            case enumChooseSubscription.FREEMIUM:
                return (
                    <div className='ac-card-subtitle'>
                        <ul className='ac-features-list ac-features-list--description'>
                            <li className='ac-feature-item'>
                                <span className='ac-feature-icon ac-feature-icon--ok'><IoCheckmark size={16} /></span>
                                <span className='ac-feature-text'>{t('subscription.freemium.feature_unlimited_jobs')}</span>
                            </li>
                            <li className='ac-feature-item'>
                                <span className='ac-feature-icon ac-feature-icon--ok'><IoCheckmark size={16} /></span>
                                <span className='ac-feature-text'>{t('subscription.freemium.feature_discover_trainings')}</span>
                            </li>
                            <li className='ac-feature-item'>
                                <span className='ac-feature-icon ac-feature-icon--ok'><IoCheckmark size={16} /></span>
                                <span className='ac-feature-text'>{t('subscription.freemium.feature_manage_projects')}</span>
                            </li>
                        </ul>
                    </div>
                );
            case enumChooseSubscription.REGULAR:
            case enumChooseSubscription.CASUAL:
                // REGULAR / CASUAL (recruiter offer) — first description, + icon, second description
                return (
                    <div className='ac-card-subtitle'>
                        <div>{t('subscription.recruiter.includes_freemium')}</div>
                        <div className='ac-plus-icon-container ac-plus-icon-container--inline' aria-hidden="true">
                            <div className='ac-plus-icon'>+</div>
                        </div>
                        <div style={{marginTop: '1rem'}}>{t('subscription.recruiter.search_profiles')}</div>
                    </div>
                );
            case enumChooseSubscription.GRANDCOMPTE:
                return (
                    <div className='ac-card-subtitle'>
                        <div>{t('subscription.grandcompte.junior_recruitment')}</div>
                    </div>
                );
            default:
                return props.description ? <div className='ac-card-subtitle'>{props.description}</div> : null;
        }
    }, [props.enumChooseSubscription, props.description, t]);

    const renderPricing = useCallback(() => {
        // If this is the SERENITE offer render its custom layout first (no radios)
        if (props.enumChooseSubscription === enumChooseSubscription.GRANDCOMPTE) {
            const serenite = props.pricingOptions && Array.isArray(props.pricingOptions) && props.pricingOptions.length > 0
                ? props.pricingOptions
                : (props.serenite || [
                    { label: 'Stagiaire', price: props.stagiairePrice || 180 },
                    { label: 'Alternant', price: props.alternantPrice || 250 },
                    { label: 'Junior', price: props.juniorPrice || 1200 }
                ]);

            return (
                <div className='ac-serenite-list'>
                    {serenite.map((s, i) => (
                        <div key={i} className='ac-serenite-pricing'>
                            <div className='ac-serenite-label'>{s.label}</div>
                            <div className='ac-serenite-price'>{s.price}€ HT</div>
                        </div>
                    ))}
                </div>
            )
        }

        // If explicit pricingOptions provided use them (covers REGULAR/CASUAL)
        if (props.pricingOptions && Array.isArray(props.pricingOptions) && props.pricingOptions.length > 0) {
            return (
                <div className='ac-pricing-options'>
                    {props.pricingOptions.map((opt, idx) => (
                        <div
                            key={idx}
                            className={`ac-pricing-option ${selectedPricingIndex === idx ? 'ac-pricing-option--selected' : ''}`}
                            onClick={() => setSelectedPricingIndex(idx)}
                        >
                            <input
                                type="radio"
                                name={`pricing-${props.title}`}
                                checked={selectedPricingIndex === idx}
                                readOnly
                            />
                            <div className='ac-pricing-option-content'>
                                <div className='ac-pricing-option-label'>{opt.label}</div>
                                <div className='ac-pricing-option-price'>{opt.price}€ HT</div>
                            </div>
                        </div>
                    ))}
                </div>
            )
        }

        // fallback: single price display if provided
        if (props.tarif) {
            return (
                <div className='ac-pricing-row'>
                    <div className='ac-price-single'>{props.tarif + t("monnaie.euro_ht_per_month")}</div>
                </div>
            )
        }

        return null;
    }, [props.enumChooseSubscription, props.pricingOptions, props.serenite, props.stagiairePrice, 
        props.alternantPrice, props.juniorPrice, props.title, props.tarif, selectedPricingIndex, t]);

    const handleButtonClick = useCallback(() => {
        if (props.pricingOptions && props.pricingOptions.length > 0) {
            props.onClick?.(props.pricingOptions[selectedPricingIndex]);
        } else {
            props.onClick?.();
        }
    }, [props.onClick, props.pricingOptions, selectedPricingIndex]);

    return (
        <div className={`abonnement-card ${props.highlight ? 'abonnement-card--highlight' : ''}`}>
            <Toast ref={toast} />
            <div className='ac-card-inner'>
                <div className='ac-card-top'>
                    <h3 className='ac-card-title ac-card-title--center'>{props.title}</h3>
                    {/* Render description based on enumChooseSubscription prop when provided */}
                    {renderDescription()}
                    {props.descriptionTitle && <div className='ac-card-plan-title'>{props.descriptionTitle}</div>}
                    {props.descriptionYear && <div className='ac-card-plan-year'>{props.descriptionYear}</div>}
                </div>

                <div className='ac-card-body'>
                    {/* plus icon is rendered inside the REGULAR/CASUAL description now */}
                    {/* features list: render if provided (freemium card uses this) */}
                    {props.features && Array.isArray(props.features) && props.features.length > 0 && (
                        <ul className='ac-features-list'>
                            {props.features.map((feature, idx) => (
                                <li key={idx} className='ac-feature-item'>
                                    <span className='ac-feature-icon ac-feature-icon--ok'><IoCheckmark size={16} /></span>
                                    <span className='ac-feature-text'>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Always render the pricing block container so the CTA stays at the bottom of the card.
                        For free (freemium) offers the container is kept empty (CSS can adjust spacing via --empty modifier). */}
                    <div className={`ac-pricing-block ${props.enumChooseSubscription === enumChooseSubscription.GRANDCOMPTE ? 'ac-pricing-block--serenite' : ''} ${props.free ? 'ac-pricing-block--empty' : ''}`}>
                        {!props.free && renderPricing()}
                    </div>

                    {/* CTA container always rendered (button or placeholder) to ensure consistent layout across cards */}
                    <div className={`ac-cta-container ${props.buttonLabel ? '' : 'ac-cta-placeholder--empty'}`}>
                        {/* If this is the current FREEMIUM offer, show the 'Offre en cours' link instead of the subscribe button */}
                        {props.isCurrent && (props.enumChooseSubscription == enumChooseSubscription.FREEMIUM) ? (
                            <div className='ac-current-link' role="button" tabIndex={0} aria-hidden="true">{t('subscription.offer_current')}</div>
                        ) : (
                            props.buttonLabel && !showSwitchArea ? (
                                <Button
                                    className='ac-cta-button'
                                    label={props.buttonLabel}
                                    onClick={handleButtonClick}
                                />
                            ) : null
                        )}

                        {showSwitchArea && (
                        <div className='ac-switch-row'>
                            <div className='ac-switch-main'>
                                <div className='ac-input-switch-label'>{inputSwitchChecked ? <Trans i18nKey={"enums.enumCompanyStatus.Active"} /> : <Trans i18nKey={"enums.enumCompanyStatus.Disabled"} />}</div>
                                <InputSwitch checked={inputSwitchChecked} onChange={onChangeInputSwitch} />
                            </div>

                            {billingDate && (
                                <div className='ac-billing-date ac-billing-date--below'>
                                    {inputSwitchChecked ? <Trans i18nKey={"subscription.subscription_term"} /> : <Trans i18nKey={"subscription.subscription_end"} />} : {new Date(billingDate).toLocaleDateString()}
                                </div>
                            )}
                        </div>
                        )}
                    </div>

                    {/* {props.descriptionSubscription && <div className='ac-description-subscription'><strong>{props.descriptionSubscription}</strong></div>} */}
                </div>
            </div>
        </div>
    )
}